import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { money } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/finance.css';

interface Doc {
  id: string;
  type: string;
  typeLabel: string;
  totalMinor: number;
  currency: string;
  status: string;
}

export function Profitability() {
  const { company } = useSession();
  const [income, setIncome] = useState<Doc[] | null>(null);
  const [costs, setCosts] = useState<Doc[] | null>(null);

  useEffect(() => {
    if (!company) return;
    setIncome(null);
    setCosts(null);
    api.get<Doc[]>('/documents?types=invoice,credit_note').then(setIncome).catch(() => setIncome([]));
    api.get<Doc[]>('/documents?types=expense,vendor_invoice,purchase_order').then(setCosts).catch(() => setCosts([]));
  }, [company]);

  const cur = company?.currency ?? 'AED';

  const totals = useMemo(() => {
    const inc = (income ?? [])
      .filter((d) => d.status !== 'void')
      .reduce((s, d) => s + (d.type === 'credit_note' ? -d.totalMinor : d.totalMinor), 0);
    const cost = (costs ?? []).filter((d) => d.status !== 'void').reduce((s, d) => s + d.totalMinor, 0);
    const margin = inc - cost;
    const marginPct = inc > 0 ? Math.round((margin / inc) * 100) : 0;
    return { inc, cost, margin, marginPct };
  }, [income, costs]);

  const byType = useMemo(() => {
    const map = new Map<string, { label: string; amount: number; kind: 'in' | 'out' }>();
    for (const d of income ?? []) {
      if (d.status === 'void') continue;
      const cur = map.get(d.type) ?? { label: d.typeLabel, amount: 0, kind: 'in' as const };
      cur.amount += d.type === 'credit_note' ? -d.totalMinor : d.totalMinor;
      map.set(d.type, cur);
    }
    for (const d of costs ?? []) {
      if (d.status === 'void') continue;
      const cur = map.get(d.type) ?? { label: d.typeLabel, amount: 0, kind: 'out' as const };
      cur.amount += d.totalMinor;
      map.set(d.type, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [income, costs]);

  const loading = !income || !costs;
  const scale = Math.max(1, totals.inc, totals.cost);

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Finanzas</span>
          <h1 className="h1">Rentabilidad</h1>
          <p className="page-head__sub">Invoiced revenue against costs, with the resulting margin. Credit notes subtract; voided items do not count.</p>
        </div>
      </div>

      {loading ? (
        <div className="faint" style={{ padding: 40 }}>Calculando…</div>
      ) : (
        <>
          <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi kpi--good">
              <div className="kpi__label">Ingresos</div>
              <div className="kpi__value">{money(totals.inc, cur)}</div>
            </div>
            <div className="kpi kpi--bad">
              <div className="kpi__label">Costes</div>
              <div className="kpi__value">{money(totals.cost, cur)}</div>
            </div>
            <div className={`kpi${totals.margin >= 0 ? ' kpi--good' : ' kpi--bad'}`}>
              <div className="kpi__label">Margen ({totals.marginPct}%)</div>
              <div className="kpi__value">{money(totals.margin, cur)}</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card__head"><span className="card__title">Ingresos frente a costes</span></div>
            <div className="card__body">
              <div className="pnl-bar">
                <div className="pnl-row">
                  <span className="pnl-row__label">Ingresos</span>
                  <span className="pnl-row__track"><span className="pnl-row__fill" style={{ width: `${(totals.inc / scale) * 100}%`, background: 'var(--accent)' }} /></span>
                  <span className="pnl-row__amt" style={{ color: 'var(--accent)' }}>{money(totals.inc, cur)}</span>
                </div>
                <div className="pnl-row">
                  <span className="pnl-row__label">Costes</span>
                  <span className="pnl-row__track"><span className="pnl-row__fill" style={{ width: `${(totals.cost / scale) * 100}%`, background: 'var(--clay)' }} /></span>
                  <span className="pnl-row__amt" style={{ color: 'var(--clay)' }}>{money(totals.cost, cur)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__head"><span className="card__title">Desglose por tipo de documento</span></div>
            <div className="card__body">
              {byType.length === 0 ? (
                <div className="empty">Sin documentos que agregar.</div>
              ) : (
                byType.map((r) => (
                  <div key={r.label} className="breakdown-row">
                    <span className="breakdown-row__label">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: r.kind === 'in' ? 'var(--accent)' : 'var(--clay)', display: 'inline-block' }} />
                      {r.label}
                    </span>
                    <span className="breakdown-row__amt" style={{ color: r.kind === 'in' ? 'var(--accent)' : 'var(--clay)' }}>
                      {r.kind === 'in' ? '+' : '−'}{money(Math.abs(r.amount), cur)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );
}
